import styled, { css } from 'styled-components';
import { Location16 } from '@elfsight/icons';
import { MediaImage } from '@elfsight/widget-media';
import { DropdownItem } from '@elfsight/widget-modal';
import { Icon, Typography } from '@elfsight/widget-ui';
import { getOptimizedImageLink } from '@elfsight/widget-utils';

interface SearchResultItemProps {
  name: string;
  address: string;
  id?: string;
  coordinates: {
    longitude: number;
    latitude: number;
  };
  image?: string;
  isExisting?: boolean;
  textColor: string;
  mutedColor: string;
  backgroundColor: string;
  onSelect: (params: {
    coordinates: { lng: number; lat: number };
    query: string;
    locationId?: string;
  }) => void;
}

const IMAGE_SIZE = 272;

export const SearchResultItem = ({
  name,
  address,
  id,
  coordinates,
  image,
  isExisting,
  textColor,
  mutedColor,
  backgroundColor,
  onSelect
}: SearchResultItemProps) => {
  const handleClick = () => {
    onSelect({
      coordinates: {
        lng: coordinates.longitude,
        lat: coordinates.latitude
      },
      query: isExisting ? name : address,
      locationId: isExisting ? id : undefined
    });
  };

  return (
    <DropdownItem onClick={handleClick}>
      <DropdownItemWrapper>
        <SearchLocationIconWrapper
          $withBorder={!!image}
          $backgroundColor={backgroundColor}
        >
          {image ? (
            <MediaImage
              src={getOptimizedImageLink(image, {
                width: IMAGE_SIZE,
                height: IMAGE_SIZE
              })}
              alt={name}
              withSkeleton
              containerWidth={'34px'}
              containerHeight={'34px'}
            />
          ) : (
            <Icon
              size={16}
              color={textColor}
              fill={textColor}
              component={Location16}
            />
          )}
        </SearchLocationIconWrapper>
        <div>
          <Typography color={textColor} textStyle={{ fontSize: 14 }}>
            {name}
          </Typography>
          <Typography color={mutedColor} textStyle={{ fontSize: 12 }}>
            {address}
          </Typography>
        </div>
      </DropdownItemWrapper>
    </DropdownItem>
  );
};

const SearchLocationIconWrapper = styled.div<{
  $backgroundColor?: string;
  $withBorder?: boolean;
}>`
  ${({ $withBorder, $backgroundColor }) => css`
    position: relative;
    min-width: 34px;
    min-height: 34px;
    background-color: ${$backgroundColor};
    overflow: hidden;
    border-radius: 8px;
    display: flex;
    justify-content: center;
    align-items: center;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      border: ${$withBorder ? `1px solid ${$backgroundColor}` : 'none'};
      border-radius: inherit;
      pointer-events: none;
    }
  `}
`;

const DropdownItemWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 12px;
`;
